// @flow
import React, { Component, type Node } from 'react';
import { connect } from 'react-redux';
import ErrorBoundary from 'app/packages/error-boundary';
import errorUtil from 'app/common/lib/errorUtil';
import ExperienceBottomBanner from 'app/common/react/components/experiences/ExperienceBottomBanner/ExperienceBottomBanner';
import ExperienceToastLaunchWrapper from 'app/common/react/components/experiences/ExperienceToast/ExperienceToastLaunchWrapper';
import get from 'lodash/get';
import HeaderHeightConsumer from 'app/common/react/components/Header/Context/ContextConsumer';
import i18n from 'app/common/lib/i18n';
import loadable from '@loadable/component';
import logInviteAttribution from 'app/packages/invite-utils/logInviteAttribution';
import ProfileHeaderContainer from 'app/common/react/components/profile/ProfileHeader/ProfileHeaderContainer';
import TimeSpent from 'app/common/lib/react/TimeSpent';
import type { AuthState as ReduxState } from 'app/common/redux/types';
import type { Experience as ExperienceType } from 'app/common/lib/react/experiences/experienceUtils';
import type { ProfileUser } from 'app/packages/pinterest-flow-types/user';
import type { Router } from 'app/common/lib/ReactRouter/types';
import type { Viewer } from 'app/packages/pinterest-flow-types/viewer';
import withPlacement from 'app/common/lib/react/experiences/withPlacement';
import withViewer from 'app/common/lib/react/withViewer';
import ZendeskChatWrapper from 'app/common/react/components/growth/smb/ZendeskChatWrapper/ZendeskChatWrapper';
import { compose } from 'redux';
import { EU_COOKIE_BAR_HEIGHT } from 'app/common/react/components/growth/unauth/header/EuCookieBar/EuCookieBar';
import { ExperienceHero } from 'app/common/react/components/experiences';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { SingleTreatmentExp } from 'app/common/lib/experiment';
import { viewingUserCreatedPins } from 'app/common/lib/BusinessUtil';
import {
  ProfileTabs,
  type ProfileTabType,
} from 'app/common/react/components/profile/ProfileHeader/ProfileTabBar';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Box, Spinner } from 'gestalt';

const FirstPinCreateProfileTakeover = loadable(() =>
  import('app/partner/business/firstAdCreate/FirstPinCreateProfileTakeover'),
);
const FirstAdCreateProfileTakeover = loadable(() =>
  import('app/partner/business/firstAdCreate/FirstAdCreateProfileTakeover'),
);

export type ProfilePageProps = {
  // Component children to show
  children: Node,
  clickedFollowingButton?: boolean,
  experience: ?ExperienceType,
  // flag used in Unauth to determine whether or not to shift margins to accomodate the eu cookie bar
  isShowEUCookieBar?: boolean,
  location: {
    query: { skipFac?: number },
  },
  // Denzel specific funtion to handle page navigation, coming from the react wrapper
  onClickTab?: (tab: ProfileTabType) => void,
  // The location to error map redux entry for this path
  renderError: Object,
  requestContext: RequestContextType,
  // The page react router passed in from the parent
  router: Router,
  setPlacementExtraContext: Function,
  showPulsar?: boolean,
  showSignupModal?: (string, string) => void,
  // The current tab on the profile page being viewed
  tab: ProfileTabType,
  // Passed in from the parent to remove the error in redux
  unsetError: () => void,
  // The resource response object for this user
  user: {
    data: ProfileUser,
    error: mixed,
    isAtEnd: boolean,
    isFetching: boolean,
    isLoaded: boolean,
  },
  // The username from the profile page routing
  username: string,
  // Viewing user from the redux store
  viewer: Viewer,
  viewerCreatedPins: boolean,
};

const HEADER_TOP_SPACING = 24;
const HEADER_TOP_SPACING_UNAUTH = 120;

class ProfilePage extends Component<ProfilePageProps> {
  constructor(props) {
    super(props);
    /* Ensure we don't unmount the First Pin Create before we complete it */
    this.initialViewerCreatedPins = props.viewerCreatedPins;
  }

  componentDidMount() {
    logInviteAttribution();
    this.trigger404Error();
  }

  UNSAFE_componentWillReceiveProps(nextProps: ProfilePageProps) {
    if (
      nextProps.clickedFollowingButton !== this.props.clickedFollowingButton &&
      nextProps.clickedFollowingButton
    ) {
      this.props.setPlacementExtraContext({ clickedFollowingButton: true });
    }
  }

  componentDidUpdate() {
    this.trigger404Error();
  }

  errorModalShown: boolean = false;
  initialViewerCreatedPins: boolean = false;

  trigger404Error() {
    const { renderError, router, unsetError, user } = this.props;
    const showBoard404Error = get(renderError, 'extra_data.resource.name') === 'BoardResource';

    if (showBoard404Error && !this.errorModalShown && user.data && !user.error) {
      this.errorModalShown = true;
      errorUtil.showHtmlError(
        interpolateNamedTemplate(i18n._("But here are some of {{ fullName }}'s other boards!"), {
          fullName: user.data.full_name,
        }).join(''),
        unsetError(),
        i18n._("We couldn't find that page."),
      );
    } else if (user.error && user.error.http_status === 404) {
      // This would happen if the 404'd board page then redirected to a 404 Profile page
      // eg., /missyjcatNotAPerson/not-a-board -> /missyjcatNotAPerson -> /?show_error=true
      router.replace('/?show_error=true');
    }
  }

  triggerRedirectToHome() {
    this.props.router.replace('/?show_error=true');
  }

  selectTab = (isOwnProfile: boolean) => {
    const { tab, user } = this.props;
    const isDefaultTopicTab = user.data.board_count === 0;

    const showCreatorProfile = user.data.show_creator_profile;
    if ([ProfileTabs.DECIDER].includes(tab) && showCreatorProfile) {
      return ProfileTabs.OVERVIEW;
    } else if (tab === ProfileTabs.OVERVIEW && !showCreatorProfile) {
      this.triggerRedirectToHome();
    } else if (tab === ProfileTabs.DECIDER && !showCreatorProfile) {
      return isDefaultTopicTab ? ProfileTabs.TOPICS : ProfileTabs.BOARDS;
    } else if (tab === ProfileTabs.ACTIVITIES && !isOwnProfile && !user.data.show_discovered_feed) {
      this.triggerRedirectToHome();
    }
    return tab;
  };

  renderBizEducationTakeover = () => {
    const { experience, location, requestContext, username } = this.props;

    const fpcTargetingExperimentEnabled = new SingleTreatmentExp(
      requestContext,
      'biz_growth_fpc_targeting',
    ).enabledWithNoActivation();

    const skipFAC = !!location.query.skipFac;
    const inFirstPinCreateExperience =
      experience &&
      experience.experience_id === PCONST.thrift.Experience.WEB_BIZ_FIRST_PIN_CREATE &&
      (!this.initialViewerCreatedPins || fpcTargetingExperimentEnabled);
    const inFirstAdCreateExperience =
      experience &&
      experience.experience_id === PCONST.thrift.Experience.WEB_BIZ_FIRST_AD_CREATE &&
      !skipFAC;

    if (inFirstPinCreateExperience) {
      return (
        <FirstPinCreateProfileTakeover
          username={username}
          experience={experience}
          fallback={
            <Box marginTop={10}>
              <Spinner
                accessibilityLabel={i18n._('Loading first pin create education', 'Loading spinner')}
                show
              />
            </Box>
          }
        />
      );
    }
    if (inFirstAdCreateExperience) {
      return (
        <FirstAdCreateProfileTakeover
          experience={experience}
          fallback={
            <Box marginTop={10}>
              <Spinner
                accessibilityLabel={i18n._('Loading first pin create education', 'Loading spinner')}
                show
              />
            </Box>
          }
        />
      );
    }
    return null;
  };

  render() {
    const {
      children,
      isShowEUCookieBar,
      onClickTab,
      requestContext,
      showPulsar,
      showSignupModal,
      user,
      username,
      viewer,
    } = this.props;

    if (!user || !user.isLoaded || !user.data) {
      return null;
    }

    const isOwnProfile = viewer.isAuth && username === viewer.username;
    const tab = this.selectTab(isOwnProfile);
    const timeSpentViewParam = isOwnProfile
      ? PCONST.thrift.ViewParameterType.USER_SELF
      : PCONST.thrift.ViewParameterType.USER_OTHERS;
    const bizEducationTakeover = isOwnProfile && this.renderBizEducationTakeover();

    i18n._('People', 'label for following category dropdown menu');
    i18n._('Boards', 'label for following category dropdown menu');

    if (user.data.show_creator_profile && viewer.isAuth) {
      return (
        <TimeSpent
          view={PCONST.thrift.ViewType.USER}
          viewParameter={timeSpentViewParam}
          objectId={user.data.id}
        >
          <React.Fragment>
            {bizEducationTakeover || (
              <div className="UserProfilePage">
                <ExperienceHero placementId={PCONST.thrift.Placement.WEB_USER_PROFILE_TAKEOVER} />
                <ProfileHeaderContainer tab={tab} onClickTab={onClickTab} user={user.data}>
                  {children}
                </ProfileHeaderContainer>
                <ExperienceBottomBanner
                  placementId={PCONST.thrift.Placement.WEB_USER_PROFILE_TAKEOVER}
                />
              </div>
            )}
            <ZendeskChatWrapper />
          </React.Fragment>
        </TimeSpent>
      );
    } else {
      const style = {
        marginTop:
          isOwnProfile || viewer.isAuth
            ? HEADER_TOP_SPACING
            : HEADER_TOP_SPACING_UNAUTH + (isShowEUCookieBar && EU_COOKIE_BAR_HEIGHT),
      };
      return (
        <TimeSpent
          view={PCONST.thrift.ViewType.USER}
          viewParameter={timeSpentViewParam}
          objectId={user.data.id}
        >
          <Box>
            <ExperienceHero placementId={PCONST.thrift.Placement.WEB_USER_PROFILE_TAKEOVER} />
            <div className="UserProfilePage" style={style}>
              <ErrorBoundary name="ProfileHeaderContainer">
                <HeaderHeightConsumer>
                  {(top) => (
                    <ProfileHeaderContainer
                      onClickTab={onClickTab}
                      requestContext={requestContext}
                      showPulsar={showPulsar}
                      showSignupModal={showSignupModal}
                      tab={tab}
                      top={top}
                      user={user.data}
                    />
                  )}
                </HeaderHeightConsumer>
              </ErrorBoundary>
              <ErrorBoundary name="UserProfileContent">
                <div className="UserProfileContent">{children}</div>
              </ErrorBoundary>
              {username === viewer.username &&
                tab === ProfileTabs.BOARDS && (
                  <ExperienceToastLaunchWrapper
                    placementId={PCONST.thrift.Placement.WEB_USER_PROFILE_TAKEOVER}
                  />
                )}
            </div>
          </Box>
        </TimeSpent>
      );
    }
  }
}

const mapStateToProps = (state: ReduxState): { clickedFollowingButton?: boolean } => {
  const { viewer, pins, topics } = state;
  const clickedFollowingButton =
    state.topics &&
    Object.keys(topics)
      .map((id) => topics[id])
      .filter((payload) => payload.showTooltip).length > 0;

  const viewerCreatedPins = viewingUserCreatedPins(viewer, pins).length !== 0;
  const location = state.location;

  return { clickedFollowingButton, viewerCreatedPins, location };
};

export default compose(
  withRequestContext,
  connect(mapStateToProps),
  withViewer,
  withPlacement(PCONST.thrift.Placement.WEB_USER_PROFILE_TAKEOVER, (props, state) => ({
    clickedFollowingButton: props.clickedFollowingButton,
  })),
)(ProfilePage);
